import {
  Box,
  Grid,
  Link,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { blue, green } from "@mui/material/colors";
import { MetricsContext, Tier, TimeFrame } from "contexts/Metrics";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ActionItem } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import Metric from "features/Home/Sidebar/MetricsHelper/Metric";
import MetricsByTemplate from "features/Home/Sidebar/MetricsHelper/Template";
import { TimePeriod } from "features/Home/Sidebar/MetricsHelper/schema";
import { trackEvent } from "utils/tracking";

interface Props {
  actionItems: ActionItem[];
}

export default function MetricsHome({ actionItems }: Props) {
  const {
    metrics,
    templateStats,
    tierStats,
    fetchMetrics,
    fetchMetricsByTier,
  } = useContext(MetricsContext);
  const { subscription } = useContext(SubscriptionContext);

  const [tab, setTab] = useState(TimeFrame.monthly);

  const getCurrentTier = () => {
    const planName =
      subscription?.subscription?.planName || subscription?.overridePlanName;
    if (planName === Plans.PRO || planName === Plans.PRO_YEARLY) {
      return Tier.pro;
    } else if (planName === Plans.VA || planName === Plans.VA_YEARLY) {
      return Tier.va;
    }
    return Tier.free;
  };

  const showAsPercentage = (value: number) => {
    return `${(value || 0).toFixed(0)}`;
  };

  const onTabChange = (_: React.SyntheticEvent, newValue: TimeFrame) => {
    trackEvent("Home Page Metrics Time Frame Changed", {
      "Time Frame": newValue,
    });
    setTab(newValue);

    if (newValue === TimeFrame.daily) {
      fetchMetrics(TimePeriod.today, true);
    } else if (
      newValue === TimeFrame.weekly &&
      metrics[TimePeriod.thisWeek] === undefined
    ) {
      fetchMetrics(TimePeriod.thisWeek, true);
    }
  };

  useEffect(() => {
    fetchMetrics(TimePeriod.thisMonth);
    fetchMetricsByTier(getCurrentTier());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typography paragraph variant="h4">
        <Box
          component="i"
          className="fa-solid fa-bullseye-arrow"
          sx={{ pr: 1, color: "secondary.main" }}
        />{" "}
        Activity Goal
      </Typography>
      {metrics[TimePeriod.thisMonth]?.outreachCount !== undefined &&
      metrics[TimePeriod.thisMonth].topTenPercent !== undefined ? (
        <Typography paragraph>
          {metrics[TimePeriod.thisMonth].outreachCount >=
          metrics[TimePeriod.thisMonth].topTenPercent ? (
            <>
              You're in the{" "}
              <Typography component="span" sx={{ color: green[500] }}>
                <strong>top 10%</strong>
              </Typography>{" "}
              of Bento users —amazing work!{" "}
              {actionItems?.length > 0 && (
                <>
                  {" "}
                  <Link
                    component={RouterLink}
                    to={`/${routes.brands}/all`}
                    sx={{ color: blue[500], textDecoration: "none" }}
                  >
                    <strong>Send more emails</strong>
                  </Link>{" "}
                  and you will likely land more brand deals.
                </>
              )}
            </>
          ) : (
            <>
              <>
                <Link
                  component={RouterLink}
                  to={`/${routes.brands}/all`}
                  sx={{ color: blue[500], textDecoration: "none" }}
                >
                  <strong>
                    Send{" "}
                    {Math.round(
                      metrics[TimePeriod.thisMonth].topTenPercent -
                        metrics[TimePeriod.thisMonth].outreachCount,
                    )}{" "}
                    more emails
                  </strong>
                </Link>{" "}
                this week and you'll be in the{" "}
                <Typography component="span" sx={{ color: green[500] }}>
                  <strong>top 10%</strong>
                </Typography>{" "}
                of Bento users!
              </>
            </>
          )}
        </Typography>
      ) : (
        <Skeleton height="35px" />
      )}
      {metrics[TimePeriod.thisMonth]?.outreachCount !== undefined &&
      tierStats !== undefined ? (
        <Grid container sx={{ mb: 4 }}>
          <Grid item xs={6}>
            How You're Doing:
          </Grid>
          <Grid item xs={6}>
            <Typography>
              {metrics[TimePeriod.thisMonth]?.outreachCount} emails sent this
              month
            </Typography>
          </Grid>
          <Grid item xs={6}>
            How Your Peers are Doing:
          </Grid>
          <Grid item xs={6}>
            <Typography>
              {showAsPercentage(tierStats.outreachCount || 0)} avg. emails sent
              this month
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Skeleton height="35px" sx={{ mb: -1 }} />
          <Skeleton height="35px" sx={{ mb: 2 }} />
        </Box>
      )}
      <Typography paragraph variant="h4">
        <Box
          component="i"
          className="fa-solid fa-chart-simple"
          sx={{ pr: 1, color: "secondary.main" }}
        />{" "}
        Metrics
      </Typography>
      <Tabs onChange={onTabChange} value={tab}>
        <Tab label="Daily" value={TimeFrame.daily} />
        <Tab label="Weekly" value={TimeFrame.weekly} />
        <Tab label="Monthly" value={TimeFrame.monthly} />
      </Tabs>
      <Box sx={{ mb: 4 }}>
        <Metric timeFrame={tab} />
      </Box>
      {templateStats?.length > 0 && (
        <Typography paragraph variant="h4">
          <Box
            component="i"
            className="fa-solid fa-file-pen"
            sx={{ pr: 1, color: "secondary.main" }}
          />{" "}
          Templates
        </Typography>
      )}
      <Box sx={{ mb: 4, maxWidth: 750 }}>
        <MetricsByTemplate />
      </Box>
    </>
  );
}
